import { Component, EventEmitter, Inject, Input, NgZone, OnDestroy, OnInit, Output, SimpleChange } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { environment } from "../../../../../../../environments/environment";
import { FileMetadata } from "../../../../../../models/file-metadata";
import { Law } from "../../../../../../models/laws";
import { Registration } from "../../../../../../models/registration";
import { RegistrationLaw } from "../../../../../../models/registration-law";
import { LawControllerService } from "../../../../../../services/laws-controller/law-controller.service";
import { LawService } from "../../../../../../services/laws-controller/law.service";
import { ServiceControllerService } from "../../../../../../services/service-controller/service-controller.service";
import { RegistrationLawControllerService } from "../../../../../../services/laws-controller/registration-law-controller.service";
import { Store } from "../../../../../../services/store.service";
import { BaseError } from "../../../../base-error";

@Component({
  selector: "law-edit",
  templateUrl: "./law-edit.component.html",
  styles: [],
  providers: [ToastrService],
})
export class LawEditComponent extends BaseError implements OnInit, OnDestroy {

  @Input() public serviceId: string;
  @Input() public law: Law;
  @Input() public registrationLaw: RegistrationLaw;
  @Input() public registration: Registration;
  @Output() public formSubmit = new EventEmitter();
  @Output() public editCancel = new EventEmitter();
  public addNew: boolean;
  public subscription: any;
  public isLoading: boolean = false;
  public lawShortName: string = "";
  public downloadLink: string;
  public fileMetadata: FileMetadata;
  private baseUrl: string = environment.BASE_URL;

  constructor(private lawControllerService: LawControllerService,
              protected serviceController: ServiceControllerService,
              @Inject(NgZone) protected zone: NgZone,
              private registrationLawControllerService: RegistrationLawControllerService,
              private lawService: LawService,
              public store: Store,
              public toastr: ToastrService) {
    super(toastr);
  }

  public ngOnInit() {
    this.subscription = this.lawService.editData.subscribe((data) => {
      this.serviceId = data.serviceId;
      this.registration = data.registration;
      this.law = data.law;
      this.registrationLaw = data.law;
      this.addNew = data.addNew;
    });
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public submit() {
    if (this.law.id === undefined) {
      this.createLaw(this.law);
    } else {
      this.saveLaw(this.law);
    }
  }

  public createLaw(law: Law) {
    if (!this.isLoading) {
      this.isLoading = true;
      this.lawControllerService.createLaw(law).subscribe(
        (data) => {
          this.addRegistrationLaw(data);
        },
        (error) => {
          this.isLoading = false;
          this.showErrorMessage(error);
        },
      );
    }
  }

  public saveLaw(law: Law) {
    if (!this.isLoading) {
      this.isLoading = true;
      this.lawControllerService.saveLaw(law).subscribe(
        (data) => {
          this.responseSuccessful("Law saved!");
          this.lawService.updateData();
          this.formSubmit.emit({ save: true, law, registrationLaw: this.registrationLaw });
          this.resetForm();
        },
        (error) => this.showErrorMessage(error),
        () => this.isLoading = false,
      );
    }
  }

  public addRegistrationLaw(law) {
    if (this.registration !== undefined) {
      this.registrationLaw = new RegistrationLaw();
      this.registrationLaw.law = law;
      this.registrationLawControllerService.createRegistrationLaw(this.registration.id, this.registrationLaw).subscribe(
        (data) => {
          this.responseSuccessful("Law added to registration");
          this.lawService.updateData();
          this.formSubmit.emit({ save: true, law, registrationLaw: this.registrationLaw });
          this.resetForm();
        },
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
        () => this.isLoading = false,
      );
    }
  }

  public uploadFileData() {
    if (this.law && this.law.fileId && this.law.id) {
      this.downloadLink = this.baseUrl + "file/" + this.law.fileId + "/binary";
      this.getFileMetadata(this.law.fileId);
    } else {
      this.downloadLink = undefined;
      this.fileMetadata = undefined;
    }
  }

  public getFileMetadata(fileId: string) {
    const getLetaAction = this.serviceId ? this.serviceController.getServiceFile(this.serviceId, fileId) : this.serviceController.getFile(fileId);
    getLetaAction.subscribe(
      (data: FileMetadata) => this.fileMetadata = data,
      (error) => this.showErrorMessageWithTitle("Get file error!", error),
    );
  }

  public download(item) {
    const downloadAction = this.serviceId ? this.serviceController.downloadServiceFile(this.serviceId, item.fileId) : this.serviceController.downloadFile(item.fileId);
    return downloadAction.subscribe((res) => {
      const pdfUrl = (window.URL || (window as any).webkitURL).createObjectURL(new Blob([res], { type: res.type }));
      const anchor = document.createElement("a");
      anchor.href = pdfUrl;
      anchor.setAttribute("download", item.fileId);
      anchor.click();
      // this.onChange.emit();
    });
  }

  public handleUpload(fileId: string, item) {
    // if previous exists then lets remove
    if (item.fileId) {
      const deleteAction = this.serviceId ? this.serviceController.deleteServiceFile(this.serviceId, item.fileId) : this.serviceController.deleteFile(item.fileId);
      deleteAction.subscribe(
          () => {
            item.fileId = undefined;
          },
          () => null,
          () => fileId ? this.saveFile(fileId, item) : null,
      );
    } else if (fileId) {
      this.saveFile(fileId, item);
    }
  }

  private saveFile(fileId: string, item) {
    setTimeout(() => {
      this.zone.run(() => {
        if (fileId) {
          item.fileId = fileId;
          this.uploadFileData();
        }
      });
    });
  }

  public deleteFile(item) {
    const deleteAction = this.serviceId ? this.serviceController.deleteServiceFile(this.serviceId, item.fileId) : this.serviceController.deleteFile(item.fileId);
    deleteAction.subscribe(
      () => {
        delete item.fileId;
        this.lawControllerService.saveLaw(item).subscribe(() => undefined);
      },
      (error) => this.showErrorMessageWithTitle("File delete error!", error),
    );
  }

  public resetForm() {
    this.law = null;
  }

  public closeLayer() {
    this.editCancel.emit({ cancel: true });
    this.resetForm();
  }

  public nameChanged(value): void {
    this.lawShortName = value;
    this.law.shortName = this.lawShortName.substring(0, 20);
  }

  public isNew(item) {
    return item === undefined;
  }

}
